import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CalendarDays } from 'lucide-react';
import { getAuthToken, clearAuthTokens } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserDashboard = () => {
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/auth');
      return;
    }

    const fetchData = async () => {
      try {
        const [userRes, sessionsRes] = await Promise.all([
          axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/matches/user/my-matches`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setUser(userRes.data);
        setSessions(sessionsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load dashboard');
        if (error.response?.status === 401) {
          clearAuthTokens();
          navigate('/auth');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="loading-state">
        <Loader2 className="animate-spin text-[#004E86]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="user-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8" data-testid="dashboard-header">
          <h1 
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-[#004E86] mb-2"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Welcome back, {user?.name}!
          </h1>
          <p className="text-lg text-gray-700">Manage your RV matches and quiz history</p>
        </div>

        {/* Quiz History */}
        <div className="bg-white rounded-lg shadow-md p-6" data-testid="quiz-history">
          <h2 className="text-2xl font-bold text-[#004E86] mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            Your Quiz History
          </h2>

          {sessions.length === 0 ? (
            <div className="text-center py-12" data-testid="no-sessions">
              <p className="text-gray-600 mb-4">You haven't taken the quiz yet!</p>
              <button
                onClick={() => navigate('/quiz')}
                className="bg-[#F2A900] text-black hover:bg-[#D99700] font-bold uppercase tracking-wide px-6 py-3 rounded-full"
                data-testid="take-quiz-button"
              >
                Take the Quiz
              </button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="sessions-list">
              {sessions.map((session, index) => (
                <div
                  key={session.session_id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-200 transition-colors cursor-pointer"
                  onClick={() => navigate(`/my-matches/${session.session_id}`)}
                  data-testid={`session-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="text-[#F2A900]" size={24} />
                      <div>
                        <p className="font-semibold text-gray-900">
                          Quiz Session {sessions.length - index}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(session.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        session.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {session.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
